import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetTransformedApplicationQuery } from "../../Store/api/application/applicationApi";
import {
  GoabCircularProgress,
  GoabCallout,
  GoabPageBlock,
  GoabSpacer,
} from "@abgov/react-components";
import { useSessionManagement } from "../../ServiceUtils/useSessionManagement";
import PortalRoot from "../../PortalRunner/global/PortalRoot";
import { URL_SEARCH_PARAMS } from "../../MPP-Common-SPA/common/constants/urlParams";
import { USER_APPLICATION_SESSION_KEY } from "../../MPP-Common-SPA/utils/globalUtility";
import { useProxyGuard } from "../../PortalRunner/hooks/useProxyGuard";

const Application = () => {
  const [params] = useSearchParams();
  const code = params.get(URL_SEARCH_PARAMS.Code);
  const { data, error, isLoading } = useGetTransformedApplicationQuery(code as string, { skip: !code });
  useProxyGuard();

  const navigate = useNavigate();
  const { getResourceValue } = useSessionManagement();

  useEffect(() => {
    if (!isLoading && error && 'status' in error && error.status === 404 && code) {
      sessionStorage.removeItem(USER_APPLICATION_SESSION_KEY);
      // If the application code is invalid (404), we clear any existing application session and redirect to the applications list page.
      navigate("/applications", { replace: true });
    }
  }, [isLoading, error, code, navigate]);

  return (
    <>
      <GoabCircularProgress
        variant="fullscreen"
        visible={isLoading}
        message={getResourceValue({ key: "RESOURCES@global.loading" })}
      />
      {(data || !code) && <PortalRoot />}
      {error && (
        <GoabPageBlock width="704px">
          <GoabSpacer vSpacing="m" />
          <GoabCallout
            type="emergency"
            heading={getResourceValue({
              key: "RESOURCES@global.error.applicationNotLocated.header",
            })}
          >
            {`${getResourceValue({
              key: "RESOURCES@global.error.applicationNotLocated.description",
              params: {
                applicationCode: code,
              },
            })}`}
          </GoabCallout>
        </GoabPageBlock>
      )}
    </>
  );
};

export default Application;
